// @ts-check
import React, { useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import {
  Box,
  Avatar as AvatarComponent,
  Button as ButtonComponent,
  Badge,
  Typography,
  List,
  ListItem,
  Grid,
} from '@material-ui/core';
import CheckCircleSharpIcon from '@material-ui/icons/CheckCircleSharp';
import CancelSharpIcon from '@material-ui/icons/CancelSharp';
import correctImg from 'assets/images/pexels-correct.png';
import blurImg from 'assets/images/pexels-blur.png';
import ImageUploader from 'sharedComponents/ImageUploader';

const ProfilePicsUpload = ({
  classes,
  title,
  imgUrl: imgUrlProp = '',
  onDoneClick,
  onRemoveClick,
  onRemove,
  isUploaded = false,
  isProfile = false,
  onClose,
  uploadToCloudinary,
  uploadProfileImage,
  onLifestylePicUpdate,
  onProfilePicUpdate,
}) => {
  const [uploadError, setUploadError] = useState(false);

  const [imgUrl, setInnerImgUrl] = React.useState(imgUrlProp);

  const setProfileImage = async (imageType, file) => {
    const imageCloudinaryUrl = await uploadProfileImage(imageType, file, uploadToCloudinary);
    setInnerImgUrl(imageCloudinaryUrl);
  };

  const renderProfilePictureUpload = () => {
    return (
      <Box display="flex" className={classes.modalContainer} data-testid="Profile">
        <Typography variant="body1" color="inherit">
          Your profile picture will be used for the client facing communication
        </Typography>
        <Typography variant="body1" color="inherit" className={classes.guidelinesHeading}>
          Guidelines and Requirements:
        </Typography>
        <List className={classes.guidelinesList}>
          <ListItem className={classes.guidelinesListItem}>
            Please use the following picture file types: <strong>JPG, PNG</strong>
          </ListItem>
          <ListItem className={classes.guidelinesListItem}>
            Please use the following picture size: <strong>max 8 MB</strong>
          </ListItem>
          <ListItem className={classes.guidelinesListItem}>
            <strong>Do not</strong> use pictures that are unprofessional
          </ListItem>
          <ListItem className={classes.guidelinesListItem}>
            <strong>Do not</strong> use blurry or unclear pictures
          </ListItem>
        </List>
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={6} className={classes.correctPicGridItem}>
            <AvatarComponent src={correctImg} alt="Correct Pic" />
            <CheckCircleSharpIcon fontSize="small" className={classes.correct} />
            <Typography variant="body1" color="inherit" className={classes.blurAndClear}>
              Professional and clear
            </Typography>
          </Grid>
          <Grid item xs={6} className={classes.correctPicGridItem}>
            <AvatarComponent src={blurImg} alt="Blur Pic" />
            <CancelSharpIcon fontSize="small" className={classes.cancel} />
            <Typography variant="body1" color="inherit" className={classes.blurAndClear}>
              Unprofessional and blurry
            </Typography>
          </Grid>
        </Grid>
        {uploadError && (
          <Typography variant="body1" color="inherit" className={classes.uploadError}>
            Photo upload error. Please upload the following picture size: max 8 MB
          </Typography>
        )}
        <ImageUploader
          extraClasses={classes}
          setUploadError={setUploadError}
          withCircleSelection
          aspectRatio={1}
          setProfileImage={setProfileImage}
          onClose={onClose}
          picType="H"
          onUpdate={onProfilePicUpdate}
        />
      </Box>
    );
  };

  const renderLifestylePictureUpload = () => {
    return (
      <Box display="flex" className={classes.modalContainer} data-testid="Lifestyle">
        <Typography variant="body1" color="inherit">
          Guidelines and Requirements:
        </Typography>
        <List className={classes.guidelinesList}>
          <ListItem className={classes.guidelinesListItem}>
            Please use the following picture file types: <strong>JPG, PNG</strong>
          </ListItem>
          <ListItem className={classes.guidelinesListItem}>
            Please use the following picture size: <strong>max 8 MB</strong>
          </ListItem>
          <ListItem className={classes.guidelinesListItem}>
            <strong>Do not</strong> use pictures that are distracting
          </ListItem>
          <ListItem className={classes.guidelinesListItem}>
            <strong>Do not</strong> use blurry or unclear pictures
          </ListItem>
        </List>
        {uploadError && (
          <Typography variant="body1" color="inherit" className={classes.uploadError}>
            Photo upload error. Please upload the following picture size: max 8 MB
          </Typography>
        )}
        <ImageUploader
          extraClasses={classes}
          setUploadError={setUploadError}
          setProfileImage={setProfileImage}
          onClose={onClose}
          picType="L"
          onUpdate={onLifestylePicUpdate}
        />
      </Box>
    );
  };

  const renderPictureUpload = () => {
    const variant = isProfile ? 'circular' : 'square';
    const picType = isProfile ? 'H' : 'L';
    const onUpdate = isProfile ? onProfilePicUpdate : onLifestylePicUpdate;

    return (
      <Box display="flex" className={classes.uploadContainer} data-testid="PictureUpload">
        {isProfile && isUploaded && (
          <>
            <Typography variant="body1" color="inherit" className={classes.uploadStatus}>
              <strong>Profile Picture Uploaded Successfully</strong>
            </Typography>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              badgeContent={<CheckCircleSharpIcon fontSize="small" className={classes.correct} />}
            >
              <AvatarComponent src={imgUrl} alt="Current Pic" variant={variant} className={cx(classes.uploadAvatar)} />
            </Badge>
          </>
        )}
        {!isProfile && isUploaded && (
          <>
            <Typography variant="body1" color="inherit" className={classes.uploadStatus}>
              <strong>Lifestyle Picture Uploaded Successfully</strong>
            </Typography>
            <Badge
              overlap="rectangular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              badgeContent={<CheckCircleSharpIcon fontSize="small" className={classes.correct} />}
            >
              <AvatarComponent src={imgUrl} alt="Current Pic" variant={variant} className={cx(classes.uploadAvatar)} />
            </Badge>
          </>
        )}
        {!isUploaded && (
          <AvatarComponent src={imgUrl} alt="Current Pic" variant={variant} className={classes.uploadAvatar} />
        )}
        {uploadError && (
          <Typography variant="body1" color="inherit" className={classes.uploadError}>
            Photo upload error. Please upload the following picture size: max 8 MB
          </Typography>
        )}
        <Box display="flex" className={classes.uploadButtonSection}>
          <ImageUploader
            extraClasses={classes}
            setUploadError={setUploadError}
            setProfileImage={setProfileImage}
            onClose={onClose}
            picType={picType}
            onUpdate={onUpdate}
            withCircleSelection={isProfile}
            aspectRatio={isProfile ? 1 : null}
          />
          <ButtonComponent
            variant="contained"
            disableElevation
            color="primary"
            className={cx(classes.uploadPicButton, classes.doneRemoveButton)}
            onClick={isUploaded ? () => onDoneClick() : () => onRemoveClick(isProfile)}
          >
            {isUploaded ? 'Done' : 'Remove'}
          </ButtonComponent>
        </Box>
      </Box>
    );
  };

  const removePicture = () => {
    return (
      <Box display="flex" className={classes.uploadContainer} data-testid="RemoveUpload">
        <Typography variant="body1" color="inherit" className={classes.uploadStatus}>
          Your picture will be deleted from the Neiman Marcus website and app
        </Typography>

        <Box display="flex" className={classes.uploadButtonSection}>
          <ButtonComponent
            variant="contained"
            disableElevation
            color="primary"
            className={classes.whiteButton}
            onClick={onClose}
          >
            Cancel
          </ButtonComponent>
          <ButtonComponent
            variant="contained"
            disableElevation
            color="primary"
            className={classes.uploadPicButton}
            onClick={onRemove}
          >
            Remove
          </ButtonComponent>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {title === 'Profile Picture Upload' && !imgUrl && renderProfilePictureUpload()}
      {title === 'Lifestyle Picture Upload' && !imgUrl && renderLifestylePictureUpload()}
      {(title === 'Profile Picture Upload' || title === 'Lifestyle Picture Upload') && imgUrl && renderPictureUpload()}
      {(title === 'Remove Profile Picture' || title === 'Remove Lifestyle Picture') && removePicture()}
    </>
  );
};

ProfilePicsUpload.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  title: PropTypes.string,
  onLifestylePicUpdate: PropTypes.func,
  onProfilePicUpdate: PropTypes.func,
};

ProfilePicsUpload.defaultProps = {
  classes: {},
  title: '',
};

export default ProfilePicsUpload;
